/**
 * Modal Component - Componente modal genérico reutilizable
 *
 * QUÉ HACE:
 * - Proporciona un contenedor modal con overlay, animaciones y accesibilidad
 * - Soporta diferentes tamaños y cierre por Escape/overlay
 * - Gestiona z-index de forma consistente en todo el proyecto
 *
 * QUÉ INVOCA: Nada externo. Es un componente presentacional puro.
 *
 * QUÉ EXPORTA: Modal (named), ModalSize (type)
 *
 * QUÉ NO DEBE HACER:
 * - NO debe contener lógica de negocio
 * - NO debe hacer llamadas a API
 * - NO debe gestionar estado propio de formularios
 *
 * REGLAS:
 * - TODO modal del proyecto DEBE usar este componente como base
 * - El z-index es fijo (z-50) para consistencia
 * - Siempre incluir aria-labels para accesibilidad
 * - El overlay usa fixed inset-0 para cubrir toda la pantalla de forma fiable
 *   en web y en WebView nativo (Capacitor Android/iOS).
 *   NO usar 100vh/100dvh inline — `inset-0` es más robusto en WebView.
 *
 * Uso:
 *   <Modal isOpen={show} onClose={close} title="Mi modal" size="md">
 *     <p>Contenido</p>
 *   </Modal>
 *
 */

import React, { ReactNode, useEffect, useCallback } from "react";
import ReactDOM from "react-dom";
import { X } from "lucide-react";
import { SAFE_AREA } from "@/lib/nativeShell";

export type ModalSize =
  | "xs"
  | "sm"
  | "md"
  | "lg"
  | "xl"
  | "2xl"
  | "3xl"
  | "full";

interface ModalProps {
  /** Controla visibilidad del modal */
  isOpen: boolean;
  /** Callback al cerrar (Escape, overlay, botón X) */
  onClose: () => void;
  /** Título mostrado en el header */
  title?: string;
  /** Subtítulo opcional debajo del título */
  subtitle?: string;
  /** Contenido principal del modal */
  children: ReactNode;
  /** Contenido del footer (botones de acción) */
  footer?: ReactNode;
  /** Tamaño del modal */
  size?: ModalSize;
  /** Deshabilita cierre al hacer clic en el overlay */
  preventOverlayClose?: boolean;
  /** Deshabilita cierre con tecla Escape */
  preventEscapeClose?: boolean;
  /** Muestra botón X en la esquina */
  showCloseButton?: boolean;
  /** Clase CSS adicional para el contenedor */
  className?: string;
  /** Clase CSS adicional para el body */
  bodyClassName?: string;
  /** Si true, no renderiza el contenedor shell (bg-white/shadow) - permite que el hijo provea su propia tarjeta */
  noShell?: boolean;
  /** Icono o elemento visual al lado del título */
  icon?: ReactNode;
  /** Reduce tipografía y padding en pantallas pequeñas (caja / POS) */
  compactOnMobile?: boolean;
  /**
   * En móvil ancla el modal abajo como una hoja a TODO el ancho (con scroll
   * interno si el contenido es largo) y termina justo sobre la barra de
   * navegación móvil, que permanece visible y operable debajo; en sm+ se
   * centra como diálogo normal.
   */
  bottomSheetOnMobile?: boolean;
}

const sizeStyles: Record<ModalSize, string> = {
  xs: "max-w-xs",
  sm: "max-w-sm",
  md: "max-w-md",
  lg: "max-w-lg",
  xl: "max-w-xl",
  "2xl": "max-w-2xl",
  "3xl": "max-w-3xl",
  full: "max-w-[95vw] max-h-[95vh]",
};

export const Modal = ({
  isOpen,
  onClose,
  title,
  subtitle,
  children,
  footer,
  size = "md",
  preventOverlayClose = false,
  preventEscapeClose = false,
  showCloseButton = true,
  className = "",
  bodyClassName = "",
  noShell = false,
  icon,
  compactOnMobile = false,
  bottomSheetOnMobile = false,
}: ModalProps) => {
  const shellCompact = [
    "app-modal-shell",
    compactOnMobile ? "caja-modal-shell-compact" : "",
  ]
    .filter(Boolean)
    .join(" ");
  // Cerrar con Escape
  const handleKeyDown = useCallback(
    (e: KeyboardEvent) => {
      if (e.key === "Escape" && !preventEscapeClose) {
        onClose();
      }
    },
    [onClose, preventEscapeClose],
  );

  useEffect(() => {
    if (isOpen) {
      document.addEventListener("keydown", handleKeyDown);
      // Prevenir scroll del body
      const prev = document.body.style.overflow;
      document.body.style.overflow = "hidden";
      return () => {
        document.removeEventListener("keydown", handleKeyDown);
        document.body.style.overflow = prev;
      };
    }
    return () => undefined;
  }, [isOpen, handleKeyDown]);

  if (!isOpen) return null;

  const handleOverlayClick = () => {
    if (!preventOverlayClose) {
      onClose();
    }
  };

  const content = (
    <div
      className={`${SAFE_AREA.modalOverlay} fixed inset-0 z-[300] flex overflow-x-hidden overflow-y-hidden bg-black/50 backdrop-blur-sm dark:bg-black/70 ${
        bottomSheetOnMobile
          ? "app-modal-overlay--bottom-sheet items-end justify-center p-0 sm:items-center sm:justify-center sm:p-4"
          : "items-center justify-center p-2.5 sm:p-4"
      }`}
      onClick={handleOverlayClick}
      role="dialog"
      aria-modal="true"
      aria-labelledby={title ? "modal-title" : undefined}
    >
      {/** If noShell is true we render children directly inside a size-constrained container
       *  allowing callers to provide their own white card/wrapper. Otherwise render the
       *  default shell (bg-white, header/footer, body). */}
      {noShell ? (
        <div
          className={`max-h-full w-full min-h-0 transform transition-all ${sizeStyles[size]} ${className}`}
          onClick={(e) => e.stopPropagation()}
        >
          {children}
        </div>
      ) : (
        <div
          className={`
            max-h-full min-h-0 bg-white shadow-xl w-full overflow-hidden
            transform transition-all
            ${
              bottomSheetOnMobile
                ? "rounded-t-2xl sm:rounded-2xl max-sm:max-h-[92dvh] dark:bg-slate-800"
                : "rounded-lg sm:rounded-2xl dark:bg-slate-800"
            }
            ${sizeStyles[size]}
            ${shellCompact}
            ${className}
          `}
          onClick={(e) => e.stopPropagation()}
        >
          {/* Header */}
          {(title || showCloseButton) && (
            <div
              className={`modal-header flex items-center justify-between gap-2 p-3 sm:p-4 border-b border-gray-100 dark:border-slate-700 ${title ? "" : "min-h-[48px]"}`}
            >
              <div className="flex items-center gap-2 sm:gap-3 flex-1 min-w-0">
                {icon && (
                  <div className="modal-header-icon flex-shrink-0 [&_svg]:max-sm:w-[18px] [&_svg]:max-sm:h-[18px]">
                    {icon}
                  </div>
                )}
                <div className="min-w-0 flex-1">
                  {title && (
                    <h3
                      id="modal-title"
                      className={`modal-title font-black text-gray-900 dark:text-gray-100 break-words leading-tight text-sm sm:text-base tracking-tight`}
                    >
                      {title}
                    </h3>
                  )}
                  {subtitle && (
                    <p className="text-[10px] sm:text-xs text-gray-500 dark:text-gray-400 mt-0.5 leading-snug">
                      {subtitle}
                    </p>
                  )}
                </div>
              </div>
              {showCloseButton && (
                <button
                  onClick={onClose}
                  className="text-gray-400 dark:text-gray-500 hover:text-gray-600 dark:hover:text-gray-300 transition-colors p-1 rounded-lg hover:bg-gray-100 dark:hover:bg-slate-700 flex-shrink-0"
                  aria-label="Cerrar modal"
                >
                  <X className="w-[18px] h-[18px] sm:w-5 sm:h-5" />
                </button>
              )}
            </div>
          )}

          {/* Body */}
          <div
            className={`modal-body p-3 sm:p-5 md:p-6 overflow-y-auto max-h-[calc(100dvh-10rem)] sm:max-h-[calc(100dvh-200px)] ${bodyClassName}`}
          >
            {children}
          </div>

          {/* Footer */}
          {footer && (
            <div
              className={`${SAFE_AREA.fixedBottom} modal-footer-actions flex items-center justify-end gap-2 sm:gap-3 px-3 sm:px-5 md:px-6 py-3 sm:py-4 border-t border-gray-100 dark:border-slate-700 bg-gray-50/50 dark:bg-slate-800/50 [&_button]:max-sm:text-xs [&_button]:max-sm:py-2`}
            >
              {footer}
            </div>
          )}
        </div>
      )}
    </div>
  );

  return ReactDOM.createPortal(content, document.body);
};
